/**
 * Open an upload in a new browser tab (same behavior as UploadsTable "Open File").
 */

const DOWNLOAD_ONLY_TYPES = ['heic', 'csv', 'xlsx', 'docx', 'dxf', 'step', 'stp'];

const isDownloadOnlyFile = (file) => {
    const extension = file.upload_name?.split('.').pop()?.toLowerCase();
    const uploadType = file.upload_type?.toLowerCase();
    return DOWNLOAD_ONLY_TYPES.some(
        (type) => extension === type || uploadType?.includes(type)
    );
};

const downloadFile = async (file, token, base_url) => {
    try {
        const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
            headers: { Authorization: token },
        });
        if (!res.ok) {
            window.showAlert(500, 'Unauthorized or error fetching file');
            return;
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.upload_name || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch {
        window.showAlert(500, 'Error fetching file');
    }
};

export function getAssociatedDesignId(file) {
    if (!file) return null;
    const assocId = file.associated_id ?? file.associatedId ?? file.association_id ?? file.associationId;
    const assocType = String(file.association_type ?? file.associationType ?? '').toLowerCase();
    if (assocType !== 'design' || assocId == null) return null;
    return assocId;
}

/**
 * @param {Array} files — project uploads from API
 * @param {number|string} designId — design.id
 * @returns {object|null} newest matching upload
 */
export function findUploadForDesign(files, designId) {
    if (!files?.length || designId == null) return null;
    const matches = files.filter((f) => {
        const assocId = getAssociatedDesignId(f);
        return assocId != null && String(assocId) === String(designId);
    });
    if (matches.length === 0) return null;
    return [...matches].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    )[0];
}

export function isSkyCivPdf(file) {
    return file?.upload_name?.endsWith('SkyCiv PDF.pdf') === true;
}

/**
 * @param {Array} files — project uploads from API
 * @param {number|string} designId — design.id
 * @returns {object|null} newest SkyCiv PDF upload for the design
 */
export function findSkyCivPdfForDesign(files, designId) {
    if (!files?.length || designId == null) return null;
    const matches = files.filter((f) => {
        if (!isSkyCivPdf(f)) return false;
        const assocId = getAssociatedDesignId(f);
        return assocId != null && String(assocId) === String(designId);
    });
    if (matches.length === 0) return null;
    return [...matches].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    )[0];
}

export async function openUploadInNewTab(file, token, base_url) {
    if (!token) {
        window.showAlert(401, 'Please login to view file');
        return;
    }

    if (isDownloadOnlyFile(file)) {
        await downloadFile(file, token, base_url);
        return;
    }

    const newWindow = window.open('about:blank', '_blank');

    if (!newWindow) {
        try {
            const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
                headers: { Authorization: token },
            });
            if (!res.ok) {
                window.showAlert(500, 'Unauthorized or error fetching file');
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            window.location.href = url;
        } catch {
            window.showAlert(500, 'Error fetching file');
        }
        return;
    }

    try {
        const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
            headers: { Authorization: token },
        });

        if (!res.ok) {
            newWindow.close();
            window.showAlert(500, 'Unauthorized or error fetching file');
            return;
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        newWindow.location.href = url;
    } catch {
        newWindow.close();
        window.showAlert(500, 'Error fetching file');
    }
}
